/**
 * CEO 2026-10-27 (opção C: "sugestão automática + fila Clientes a revisar") —
 * as listas do eDSP (clientes, faturada, reparações) não trazem tipo, SIRET
 * nem TVA; antes todo cliente novo virava Profissional. O Gestion SUGERE o
 * tipo e o escritório confirma na fila. Fonte única (API e tela).
 *
 * Regra, nesta ordem:
 *  1. SIRET ou TVA → Profissional;
 *  2. forma jurídica ou palavra de empresa no nome (GARAGE, CARROSSERIE,
 *     SARL, SAS, AUTO, marca de carro…), dígito ou "&" → Profissional;
 *  3. "M.", "MME", "MONSIEUR"… ou 2 a 4 palavras só de letras → Particular;
 *  4. resto (ex.: uma palavra só, "TILBURY") → Profissional, sem certeza.
 */
export type TipoSugerido = "individual" | "business";
export type MotivoSugestao = "siret_tva" | "nome_empresa" | "nome_pessoa" | "indefinido";

/** "S.A.R.L.", "S.A.S", "E.U.R.L." (sem pontos já caem em PALAVRAS_EMPRESA). */
const FORMA_JURIDICA_COM_PONTOS =
  /(^|[^A-Z])(S\.A\.R\.L|S\.A\.S|E\.U\.R\.L|S\.N\.C|S\.C\.I)\.?(?![A-Z])/;

const PALAVRAS_EMPRESA = new Set([
  // forma jurídica / estrutura
  "SA",
  "SARL",
  "SAS",
  "SASU",
  "EURL",
  "SNC",
  "SCI",
  "SCM",
  "EI",
  "ETS",
  "ETABLISSEMENT",
  "ETABLISSEMENTS",
  "STE",
  "SOCIETE",
  "CIE",
  "GROUPE",
  "GROUP",
  "HOLDING",
  "ENTREPRISE",
  "ENTREPRISES",
  "AGENCE",
  "CABINET",
  "ATELIER",
  "ATELIERS",
  "CENTRE",
  "CENTER",
  // ramo automóvel
  "GARAGE",
  "GARAGES",
  "CARROSSERIE",
  "CARROSSERIES",
  "CARROSSIER",
  "AUTO",
  "AUTOS",
  "AUTOMOBILE",
  "AUTOMOBILES",
  "AUTOMOTIVE",
  "MOTOR",
  "MOTORS",
  "CAR",
  "CARS",
  "MECANIQUE",
  "DEPANNAGE",
  "PNEU",
  "PNEUS",
  "PIECES",
  "OCCASION",
  "OCCASIONS",
  "CONCESSION",
  "VO",
  "VN",
  "PEINTURE",
  "DEBOSSELAGE",
  "GRELE",
  "PDR",
  "DSP",
  "REPARE",
  "REPARATION",
  "EXPERT",
  "EXPERTISE",
  "EXPERTS",
  "ASSURANCE",
  "ASSURANCES",
  "LEASING",
  "LOCATION",
  "RENT",
  "RENTAL",
  "FLOTTE",
  "PARC",
  "TRANSPORT",
  "TRANSPORTS",
  "LOGISTIQUE",
  "LOGISTICS",
  "AMBULANCE",
  "AMBULANCES",
  "TAXI",
  "TAXIS",
  "SERVICE",
  "SERVICES",
  "DISTRIBUTION",
  "COMMERCE",
  "INTERNATIONAL",
  "FRANCE",
  "PREMIUM",
  "SPORT",
  "CLINIC",
  "CLINIQUE",
  "PRO",
  "TECHNIC",
  "TECHNICS",
  "SPEED",
  "CITY",
  "STORM",
  "STORMS",
  "FINANCE",
  "FINANCES",
  "MANAGEMENT",
  "CONSULTING",
  "SOLUTIONS",
  "CONCEPT",
  "EXCLUSIVE",
  "SELF",
  "POINT",
  "PLUS",
  "GLASS",
  "CARGLASS",
  "VITRAGE",
  "MONDIAL",
  "NORAUTO",
  "SPEEDY",
  "MIDAS",
  "EUROMASTER",
  "LEASE",
  "ARVAL",
  "AYVENS",
  "HERTZ",
  "AVIS",
  "EUROPCAR",
  "SIXT",
  "CONTROLE",
  "MOTO",
  "MOTOS",
  // marcas (concessionárias)
  "RENAULT",
  "PEUGEOT",
  "CITROEN",
  "TOYOTA",
  "BMW",
  "MERCEDES",
  "AUDI",
  "VOLKSWAGEN",
  "VW",
  "FORD",
  "OPEL",
  "FIAT",
  "NISSAN",
  "KIA",
  "HYUNDAI",
  "SKODA",
  "SEAT",
  "DACIA",
  "VOLVO",
  "TESLA",
  "HONDA",
  "SUZUKI",
  "MITSUBISHI",
  "LEXUS",
  "JEEP",
  "PORSCHE",
  "MINI",
  "ALFA",
  "SUBARU",
  "IVECO",
  "MAZDA",
  "CUPRA",
  "LAND",
  "ROVER",
  "JAGUAR",
]);

const TRATAMENTOS = new Set(["M", "MR", "MME", "MLLE", "MONSIEUR", "MADAME", "MADEMOISELLE"]);

/** Começos de palavra de empresa ("CARROSSE", "CARROSSER", "AUTOMOB"…). */
const PREFIXOS_EMPRESA = [
  "CARROSS",
  "AUTOMOB",
  "GARAG",
  "MECANI",
  "DEPANN",
  "PAREBRIS",
  "PARABRIS",
  "DEGREL",
  "DEBOSS",
];

/**
 * Nome curto que começa com artigo ("LA STRADA") é nome de loja; com 3+
 * palavras pode ser sobrenome ("LA MONICA SANDRA").
 */
const ARTIGOS_INICIAIS = new Set(["LA", "LES", "AU", "AUX", "L"]);

const semAcento = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "");

export function sugerirTipoCliente(c: {
  nome: string;
  siret?: string | null;
  tva?: string | null;
  /** Razão social: "BAIA ALAIN" com razão "GARAGE DE L EUROPE" é empresa. */
  razaoSocial?: string | null;
}): { kind: TipoSugerido; motivo: MotivoSugestao } {
  if ((c.siret ?? "").trim() || (c.tva ?? "").trim()) {
    return { kind: "business", motivo: "siret_tva" };
  }
  const razao = (c.razaoSocial ?? "").trim();
  if (razao && razao.toUpperCase() !== (c.nome ?? "").trim().toUpperCase()) {
    if (sugerirTipoCliente({ nome: razao }).motivo === "nome_empresa") {
      return { kind: "business", motivo: "nome_empresa" };
    }
  }
  const nome = semAcento(c.nome ?? "")
    .toUpperCase()
    .trim();
  if (!nome) return { kind: "business", motivo: "indefinido" };
  if (/[0-9&]/.test(nome) || FORMA_JURIDICA_COM_PONTOS.test(nome)) {
    return { kind: "business", motivo: "nome_empresa" };
  }
  const palavras = nome.split(/[^A-Z]+/).filter(Boolean);
  const pareceEmpresa =
    palavras.some((p) => PALAVRAS_EMPRESA.has(p)) ||
    palavras.some((p) => PREFIXOS_EMPRESA.some((pre) => p.startsWith(pre))) ||
    (ARTIGOS_INICIAIS.has(palavras[0] ?? "") && palavras.length <= 2);
  if (pareceEmpresa) return { kind: "business", motivo: "nome_empresa" };
  if (palavras.length > 0 && TRATAMENTOS.has(palavras[0]!)) {
    return { kind: "individual", motivo: "nome_pessoa" };
  }
  // Letra solta no meio do nome ("POINT S SENS") é sigla de empresa.
  // (D'ALMEIDA não conta: a letra está colada no sobrenome.)
  if (nome.split(/\s+/).some((p) => /^[A-Z]\.?$/.test(p))) {
    return { kind: "business", motivo: "nome_empresa" };
  }
  const soLetras = nome.split(/\s+/).every((p) => /^[A-Z][A-Z'-]*$/.test(p));
  if (soLetras && palavras.length >= 2 && palavras.length <= 4) {
    return { kind: "individual", motivo: "nome_pessoa" };
  }
  return { kind: "business", motivo: "indefinido" };
}
